<template>
  <AutoCompleteShell
    ref="autocomplete"
    :label="label"
    :name="label"
    :hint="hint ?? undefined"
    variable-height
    :disabled="isDisabled"
    :error-messages="allErrors"
    :placeholder="placeholder"
    @open="restoreToPreviousState"
    @close="onClose"
    @close-expand="onCloseExpand"
    @clear="clear"
    @update:search="updateSearch"
  >
    <template #popover>
      <div v-if="parentName" class="header">
        <vs-button
          small
          type="secondary"
          icon="mdi-chevron-left"
          :label="parentName"
          @click.stop="navigateBack"
        />
      </div>
      <picker-list-virtualized ref="virtualizedList" :items="items" @near-bottom="continueFetching">
        <template #item="{ item }">
          <picker-list-item
            tabindex="-1"
            :item="item"
            :is-selected="isSelected(item)"
            :prepend-icon="false"
            dense
            :truncate="false"
            class="py-1"
            @select="selectItem"
            @expand="navigateIntoItem"
          />
        </template>
      </picker-list-virtualized>
      <div v-if="noResultsMessage" class="no-data">
        <v-container class="my-4">
          <v-row justify="center">
            <vs-text center type="subdued">{{ noResultsMessage }}</vs-text>
          </v-row>
        </v-container>
      </div>
      <span v-if="allowCustomValue && search && !isSearchTextInItems">
        <v-divider class="mb-2" />
        <v-list-item data-test="create-tile" @click.stop="handleCustomInputClick">
          <v-list-item-content>
            <vs-text over-flow-wrap="anywhere">{{ customValueButtonLabel }}</vs-text>
          </v-list-item-content>
        </v-list-item>
      </span>
    </template>
  </AutoCompleteShell>
</template>

<script setup lang="ts">
import { computed, nextTick, Ref, ref, toRef, watch } from 'vue'
import AutoCompleteShell from '@/components/vision/next/inputs/AutoCompleteShell.vue'
import PickerListItem from '@/components/vision/next/picker-list/PickerListItem.vue'
import PickerListVirtualized from '@/components/vision/next/picker-list/PickerListVirtualized.vue'
import {
  HeadlessAutoCompleteValueType,
  ValueResolver,
} from '@/components/vision/next/inputs/useAutoComplete'
import VsText from '@/components/vision/VsText.vue'
import { useFormField } from '@/components/vision/next/forms/useFormAdapter'
import {
  SearchOptions,
  useNewAutocomplete,
} from '@/components/vision/next/inputs/UseNewAutocomplete'
import { PickerItemType } from '@/components/vision/next/picker-list/models'

const props = withDefaults(
  defineProps<{
    value?: string | number | null
    hint?: string | null
    isDisabled?: boolean
    label: string
    placeholder?: string
    useSearchItems: (options: Ref<SearchOptions>) => {
      items: Ref<Readonly<PickerItemType[]>>
      hasNextPage: Ref<boolean>
      fetchNextPage: (options?: any) => any
      isFetching: Ref<boolean>
      isFetched: Ref<boolean>
      isInitialLoading: Ref<boolean>
    }
    valueResolver: ValueResolver
    allowCustomValue?: boolean
    required?: boolean
    customValidationRules?: ((value: any) => string | undefined)[]
    errorMessages?: string[]
  }>(),
  {
    value: null,
    isDisabled: false,
    allowCustomValue: false,
    required: false,
    hint: null,
  },
)

const valueAsRef = toRef(props, 'value')
const requiredAsRef = toRef(props, 'required')
const emit = defineEmits<{
  (e: 'selected', id: any): void
}>()
const handleCustomInputClick = () => {
  onValueChanged({
    id: search.value,
    title: search.value,
  })
  autocomplete.value?.close()
}
const isSelected = (item: HeadlessAutoCompleteValueType) => {
  return item.id === valueAsRef.value
}
const autocomplete = ref()
const virtualizedList = ref()
const onNavigate = () => {
  autocomplete.value?.input?.focus()
}
const scrollToPosition = (pos: number) => {
  virtualizedList?.value?.scrollToPosition(pos)
}

const onValueChanged = (incoming?: HeadlessAutoCompleteValueType) => {
  if (incoming && incoming.id === valueAsRef.value) {
    return
  }
  emit('selected', incoming?.id)
  if (incoming) {
    autocomplete.value?.close(false)
  }
}

const {
  items,
  updateSearch,
  selectItem,
  clear,
  clearSearch,
  search,
  continueFetching,
  noResultsMessage,
  navigateIntoItem,
  navigateBack,
  restoreToPreviousState,
  parentName,
} = useNewAutocomplete({
  onNavigate,
  scrollToPosition,
  valueResolver: props.valueResolver,
  onValueChanged,
  value: valueAsRef,
  useSearchItems: props.useSearchItems,
})

const { allErrorMessages: remoteErrors, runValidation } = useFormField({
  value: valueAsRef,
  required: requiredAsRef.value,
  localRules: props.customValidationRules,
})

const customValueButtonLabel = computed(() => `New "${search.value}"`)
const isSearchTextInItems = computed(() => items.value.some((item) => item.title === search.value))

const allErrors = computed(() => [...remoteErrors.value, ...(props.errorMessages ?? [])])

const onClose = () => {
  nextTick().then(() => runValidation())
}

const onCloseExpand = (enableEmitCustomSearchValue = true) => {
  if (props.allowCustomValue && search.value && enableEmitCustomSearchValue) {
    emit('selected', search.value)
    autocomplete.value?.setModified(false)
    nextTick().then(() => runValidation())
  } else {
    clearSearch()
  }
}

watch(
  () => props.value,
  (newValue, oldValue) => {
    if (newValue !== oldValue && !newValue) {
      clear()
    }
  },
)

defineExpose({
  clear,
  search,
  focus: () => autocomplete.value?.focus(),
})
</script>

<style scoped>
.no-data {
  flex-grow: 1;
  height: 100%;
  width: 100%;
}
.header {
  padding: var(--space-small);
}
</style>
